import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import { PROFILE_IMG_BASE, API_BASE } from "@config/env";
import AvailabilityRing from "@common/AvailabilityRing";
import axios from "axios";

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => fd.append(k,v));
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

const PER_PAGE = 10;
const COLS = "minmax(200px,2.5fr) minmax(120px,1.2fr) 110px minmax(120px,1.2fr) 130px";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d.replace(/-/g, "/")).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });
};
const fmtTime = (d) => {
  if (!d) return "";
  return new Date(d.replace(/-/g, "/")).toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" });
};

export default function AvailabilityUpdatesPage() {
  const { userSlug } = useAuth();
  const navigate = useNavigate();

  const [updates,    setUpdates]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  useEffect(() => {
    if (!userSlug) return;
    setLoading(true);
    post("get-recent-availability-updates", {
      user_slug: userSlug,
      limit: PER_PAGE,
      offset: (page - 1) * PER_PAGE,
    }).then(res => {
      if (res?.status === 1) {
        setUpdates(res.updates || []);
        setTotal(res.total || 0);
      }
      setLoading(false);
    });
  }, [userSlug, page]);

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>navigate("/dashboard")}
            style={{ width:34, height:34, borderRadius:9, border:"1px solid var(--fc-border)", background:"var(--fc-card-bg)", cursor:"pointer", color:"var(--fc-text-muted)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <i className="fa-solid fa-arrow-left" style={{fontSize:13}} />
          </button>
          <div>
            <h1 style={{ fontSize:19, fontWeight:800, color:"var(--fc-text-primary)", margin:0, letterSpacing:"-0.01em" }}>Availability Updates</h1>
            <p style={{ fontSize:12.5, color:"var(--fc-text-subtle)", margin:"2px 0 0" }}>Audit trail of candidate availability changes across your team</p>
          </div>
        </div>
        {total > 0 && (
          <div style={{ fontSize:12.5, fontWeight:600, color:"var(--fc-text-muted)", background:"var(--fc-bg-light)", border:"1px solid var(--fc-border)", borderRadius:8, padding:"6px 12px" }}>
            {total} total record{total!==1?"s":""}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"60px 24px", textAlign:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:18, color:"var(--fc-text-subtle)" }} />
        </div>
      ) : updates.length === 0 ? (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"60px 24px", textAlign:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ width:56, height:56, borderRadius:16, background:"var(--fc-border-light)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
            <i className="fa-solid fa-clock-rotate-left" style={{ fontSize:22, color:"var(--fc-text-hint)" }} />
          </div>
          <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:5 }}>No availability updates yet</div>
          <div style={{ fontSize:13, color:"var(--fc-text-subtle)" }}>Changes will show up here as your team updates candidate availability.</div>
        </div>
      ) : (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>

          {/* Header row */}
          <div style={{ display:"grid", gridTemplateColumns:COLS, padding:"10px 20px", background:"var(--fc-bg-light)", borderBottom:"1.5px solid var(--fc-border)", gap:0 }}>
            {[["Candidate","left"],["Role","left"],["Availability","center"],["Updated By","left"],["Date","right"]].map(([h,a]) => (
              <div key={h} style={{ fontSize:10, fontWeight:700, color:"var(--fc-text-subtle)", textTransform:"uppercase", letterSpacing:"0.09em", textAlign:a }}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {updates.map((u, i) => {
            const isLast = i === updates.length - 1;
            return (
              <div key={u.user_slug || i}
                onClick={() => navigate(`/resources/details/${u.user_slug}`)}
                style={{ display:"grid", gridTemplateColumns:COLS, alignItems:"center", padding:"13px 20px", borderBottom:isLast?"none":"1px solid var(--fc-border-light)", cursor:"pointer", transition:"background 0.1s", gap:0 }}
                onMouseEnter={e=>e.currentTarget.style.background="var(--fc-bg-light)"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>

                {/* Candidate */}
                <div style={{ display:"flex", alignItems:"center", gap:12, paddingRight:16, minWidth:0 }}>
                  <div style={{ width:42, height:42, borderRadius:11, background:"var(--fc-border-light)", border:"1px solid var(--fc-border)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden" }}>
                    {u.profile_image_path
                      ? <img src={PROFILE_IMG_BASE + u.profile_image_path} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>{e.target.style.display="none"; if(e.target.nextSibling) e.target.nextSibling.style.display="flex";}} />
                      : null}
                    <i className="fa-solid fa-user" style={{ fontSize:16, color:"var(--fc-text-subtle)", display:u.profile_image_path?"none":"flex" }} />
                  </div>
                  <div style={{ minWidth:0, flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", lineHeight:1.35 }}>
                      {u.resource_name}
                    </div>
                  </div>
                </div>

                {/* Role */}
                <div style={{ paddingRight:12, minWidth:0 }}>
                  <span style={{ fontSize:12, fontWeight:600, color: u.user_profile_role ? "var(--fc-primary)" : "var(--fc-text-hint)", fontStyle: u.user_profile_role ? "normal" : "italic", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"block" }}>
                    {u.user_profile_role || "No role set"}
                  </span>
                </div>

                {/* Availability */}
                <div style={{ display:"flex", justifyContent:"center" }}>
                  {u.availability != null
                    ? <AvailabilityRing value={u.availability} size={40} />
                    : <span style={{ fontSize:12, color:"var(--fc-border)" }}>—</span>}
                </div>

                {/* Updated by */}
                <div style={{ paddingRight:12, minWidth:0 }}>
                  {u.updated_by_name ? (
                    <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:"var(--fc-text-muted)" }}>
                      <i className="fa-solid fa-user-pen" style={{ fontSize:9, color:"var(--fc-text-hint)", flexShrink:0 }} />
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.updated_by_name}</span>
                    </div>
                  ) : <span style={{ fontSize:12, color:"var(--fc-border)" }}>—</span>}
                </div>

                {/* Date */}
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:12.5, fontWeight:700, color:"var(--fc-text-primary)" }}>{fmtDate(u.updated_at)}</div>
                  <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:1 }}>{fmtTime(u.updated_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, flexWrap:"wrap", gap:10 }}>
          <span style={{ fontSize:13, color:"var(--fc-text-muted)" }}>
            Showing <strong style={{color:"var(--fc-text-primary)"}}>{(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE,(page-1)*PER_PAGE+updates.length)}</strong> of <strong style={{color:"var(--fc-text-primary)"}}>{total}</strong>
          </span>
          <div style={{ display:"flex", gap:5, alignItems:"center" }}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
              style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===1?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===1?0.4:1,transition:"all 0.15s" }}>
              <i className="fa-solid fa-chevron-left" style={{fontSize:10}} />
            </button>
            {Array.from({length:totalPages},(_,i)=>i+1).map(p=>{
              const show=p===1||p===totalPages||(p>=page-1&&p<=page+1);
              const dot=(p===page-2&&page>3)||(p===page+2&&page<totalPages-2);
              if(dot) return <span key={p} style={{color:"var(--fc-text-hint)",padding:"0 2px",fontSize:14,lineHeight:1}}>···</span>;
              if(!show) return null;
              return (
                <button key={p} onClick={()=>setPage(p)}
                  style={{ width:34,height:34,borderRadius:8,border:`1.5px solid ${p===page?"var(--fc-primary)":"var(--fc-border)"}`,background:p===page?"var(--fc-primary)":"var(--fc-card-bg)",color:p===page?"#fff":"var(--fc-text-muted)",fontSize:12,fontWeight:p===page?700:400,cursor:"pointer",transition:"all 0.15s" }}>
                  {p}
                </button>
              );
            })}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ width:34,height:34,borderRadius:8,border:"1.5px solid var(--fc-border)",background:"var(--fc-card-bg)",cursor:page===totalPages?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--fc-text-muted)",opacity:page===totalPages?0.4:1,transition:"all 0.15s" }}>
              <i className="fa-solid fa-chevron-right" style={{fontSize:10}} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
